import React, { FC, FunctionComponent } from 'react';
import { useAuthManager } from '@funtech-int/yango-shared';

import { Button } from '@/shared/components/Button/Button';
import { getAvatarsImageUrl } from '@/shared/lib/images';
import { useHasSubscription } from '@/shared/hooks/useHasSubscription';
import { useProfileManager } from '@/shared/hooks/useProfileManager';
import { useCurrentUrl } from '@/shared/hooks/useCurrentUrl';
import { clientConfig } from '@/config/client';

import { useI18n } from '../../hooks/useI18n';
import { BrandAvatar } from '../BrandAvatar/BrandAvatar';

import { AvatarWithBadges } from './_AvatarWrapper';
import { UserIdMenu } from './_UserIdMenu';
import styles from './Profile.module.css';

const AVATAR_URL_SIZE = '80x80';

interface ProfileProps {
    dropDownMenuClassName: string;
}

const {
    userIdConfig: { withGolden },
} = clientConfig;

const Profile: FunctionComponent<ProfileProps> = ({ dropDownMenuClassName }) => {
    const i18n = useI18n();
    const authManager = useAuthManager();
    const currentUrl = useCurrentUrl();
    // by default redirects back to window.location.origin
    const logoutLink = authManager.getLogoutUrl(currentUrl).toString();

    const hasSubscription = useHasSubscription();
    const profileManager = useProfileManager();

    return (
        <div className={styles.root}>
            <AvatarWithBadges
                className={styles.dropdown}
                avatarIcon={
                    <button
                        aria-label={i18n('profile__menu_aria_label')}
                        className={styles.avatarButton}
                    >
                        <BrandAvatar
                            avatarSize={48}
                            avatarUrl={getAvatarsImageUrl(
                                profileManager.userProfile?.avatarsUrl || '',
                                AVATAR_URL_SIZE
                            )}
                            hasSubscriptions={hasSubscription}
                            isMoreContrastDefaultAvatar
                        />
                    </button>
                }
                goldenStatus={withGolden && Boolean(profileManager.userProfile?.badges?.golden)}
            >
                <UserIdMenu
                    dropDownMenuClassName={dropDownMenuClassName}
                    logoutLink={logoutLink}
                    addMultiAccountLink={authManager.getAddAccountUrl(currentUrl).toString()}
                />
            </AvatarWithBadges>
        </div>
    );
};

interface ProfileMenuProps {
    dropDownMenuClassName: string;
    onLoginClick?: () => void;
}

interface LoginLinkProps {
    onClick?: () => void;
}

const LoginLink: FC<LoginLinkProps> = ({ onClick }) => {
    const authManager = useAuthManager();
    const currentUrl = useCurrentUrl();
    const i18n = useI18n();

    return (
        <Button
            as="a"
            variant="accentWhite"
            theme="dark"
            size={32}
            onClick={onClick}
            href={authManager.getLoginUrl(currentUrl).toString()}
        >
            {i18n('profile__login_title')}
        </Button>
    );
};

export const ProfileContainer: FC<ProfileMenuProps> = ({ dropDownMenuClassName, onLoginClick }) => {
    const { isAuthorized } = useAuthManager();

    if (!isAuthorized) {
        return <LoginLink onClick={onLoginClick} />;
    }

    return <Profile dropDownMenuClassName={dropDownMenuClassName} />;
};
